import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Card, Tab, Nav, Row, Col } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import api from "../api/api";
import { toast } from "react-toastify";

const Settings = () => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const [userId, setUserId] = useState(null);
  const [userVerified, setUserVerified] = useState(false);

  // Get user ID when component mounts or currentUser changes
  useEffect(() => {
    if (currentUser && currentUser._id) {
      setUserId(currentUser._id);
      console.log("Current user ID set:", currentUser._id);
      verifyUserId(currentUser._id);
    } else {
      // Try to get user from localStorage as a fallback
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (storedUser && storedUser._id) {
          setUserId(storedUser._id);
          console.log("User ID from localStorage:", storedUser._id);
          verifyUserId(storedUser._id);
        }
      } catch (err) {
        console.error("Error getting user from localStorage:", err);
        setError("Failed to retrieve user session. Please log in again.");
      }
    }
  }, [currentUser]);

  // Verify if the user ID exists in the database
  const verifyUserId = async (id) => {
    if (!id) return;

    try {
      console.log("Verifying user ID:", id);
      const response = await api.get(`/users/verify/${id}`);
      console.log("User verification response:", response.data);
      setUserVerified(true);
    } catch (err) {
      console.error("User verification failed:", err);
      if (err.response?.status === 404) {
        setError("User account not found. Please log in again.");
        setUserVerified(false);
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Check if user is authenticated
    if (!userId) {
      console.error("No user ID available");
      setError("User session expired. Please log out and log in again.");
      setLoading(false);
      return;
    }

    // Check if user is verified
    if (!userVerified) {
      console.error("User not verified");
      setError(
        "User account could not be verified. Please log out and log in again."
      );
      setLoading(false);
      return;
    }

    console.log("Submitting with user ID:", userId);

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match");
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.newPassword.length < 6) {
      setError("New password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      console.log("Sending password change request");
      // Show current password for debugging
      console.log(
        "Current password (masked):",
        "*".repeat(formData.currentPassword.length)
      );
      console.log(
        "New password (masked):",
        "*".repeat(formData.newPassword.length)
      );

      // Use the api client instead of axios directly
      const response = await api.post("/users/change-password", {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        userId: userId,
      });

      console.log("Password change successful:", response.data);
      toast.success("Password changed successfully!");
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Password change error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMessage = "Failed to change password. Please try again.";

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Settings</h2>

      <Tab.Container id="settings-tabs" defaultActiveKey="password">
        <Row>
          <Col md={3}>
            <Nav variant="pills" className="flex-column mb-4">
              <Nav.Item>
                <Nav.Link eventKey="password">Password</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="profile" disabled>
                  Profile (Coming Soon)
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="notifications" disabled>
                  Notifications (Coming Soon)
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          <Col md={9}>
            <Tab.Content>
              <Tab.Pane eventKey="password">
                <Card>
                  <Card.Header>
                    <h4>Change Password</h4>
                  </Card.Header>
                  <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {!userVerified && !error && (
                      <Alert variant="warning">
                        Verifying your account... If this message persists,
                        please try logging out and back in.
                      </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Current Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="currentPassword"
                          value={formData.currentPassword}
                          onChange={handleChange}
                          required
                          disabled={!userVerified}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleChange}
                          required
                          disabled={!userVerified}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Confirm New Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          disabled={!userVerified}
                        />
                      </Form.Group>

                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || !userVerified}
                      >
                        {loading ? "Changing Password..." : "Change Password"}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </div>
  );
};

export default Settings;
